import React, { useEffect, useState } from "react";
import { Client } from "../../interfaces/client";
import { Order } from "../../interfaces/order";
import { CriminalOrder } from "../../interfaces/criminalOrder";
import {
  getCriminalOrderByOrderID,
  sendCriminalFinalReport,
  sendCriminalFinalReportSuran,
} from "../../services/criminalOrderService";
import {
  getReferenceOrderByOrderID,
  sendReferenceFinalReport,
} from "../../services/referenceOrderService";
import { updateOrderStatus } from "../../services/orderService";
import { ReferenceOrder } from "../../interfaces/referenceOrder";

interface UploadSendFinalReportPopupProps {
  client: Client;
  order: Order;
  criminalOrder?: CriminalOrder;
  referenceOrder?: ReferenceOrder;
  onClose: (message?: string) => void;
}

const UploadSendFinalReportPopup: React.FC<UploadSendFinalReportPopupProps> = ({
  client,
  order,
  criminalOrder,
  referenceOrder,
  onClose,
}) => {
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loadingSpinner, setLoadingSpinner] = useState(true);
  const [byMail, setByMail] = useState(false);

  const DELIVERY_OPTION_MAP: Record<number, string> = {
    1: "Email",
    2: "Fax",
    3: "Mail",
  };

  useEffect(() => {
    const getInitialValues = async () => {
      setErrorMessage(null);
      const mailPreference = client.deliveryOptionID === 3 ? true : false;
      setByMail(mailPreference);
      setLoadingSpinner(false);
    };

    getInitialValues();
  }, [client]);

  const handleFormUpload = async () => {
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = ".pdf";
    fileInput.click();
    fileInput.onchange = async (event: any) => {
      const file = event.target.files[0];
      if (!file) return;
      setPdfBlob(file);
      const url = URL.createObjectURL(file);
      setPdfUrl(url);
    };
  };

  const sendFinalReport = async () => {
    if (!pdfBlob) {
      setErrorMessage("Please generate the report before sending.");
      return;
    }
    try {
      setIsSending(true);
      setLoadingSpinner(true);
      let response;
      if (criminalOrder) {
        if (client.abbreviation === "784" || client.abbreviation === "785") {
          response = await sendCriminalFinalReportSuran(
            client,
            criminalOrder,
            pdfBlob,
            byMail,
          );
        } else {
          response = await sendCriminalFinalReport(
            client,
            criminalOrder,
            pdfBlob,
            byMail,
          );
        }
      } else if (referenceOrder) {
        response = await sendReferenceFinalReport(
          client,
          referenceOrder,
          pdfBlob,
          byMail,
        );
      }

      // mark order as complete if all final reports have been sent
      if (order) {
        const referenceOrder = await getReferenceOrderByOrderID(order.orderID);
        const criminalOrder = await getCriminalOrderByOrderID(order.orderID);

        const isReferenceComplete =
          referenceOrder.referenceOrderID !== 0 &&
          referenceOrder.finalReportDate !== null;
        const isCriminalComplete =
          criminalOrder.criminalOrderID !== 0 &&
          criminalOrder.finalReportUrl !== null;

        if (
          (referenceOrder &&
            criminalOrder &&
            isReferenceComplete &&
            isCriminalComplete) ||
          (referenceOrder.referenceOrderID !== 0 &&
            criminalOrder.criminalOrderID === 0 &&
            isReferenceComplete) ||
          (referenceOrder.referenceOrderID === 0 &&
            criminalOrder.criminalOrderID !== 0 &&
            isCriminalComplete)
        ) {
          await updateOrderStatus(order.orderID, 4);
        }
      }

      if (response) {
        onClose();
      } else {
        throw new Error("Failed to send the final report.");
      }
    } catch (error) {
      setErrorMessage("Failed to send the final report.");
    } finally {
      setLoadingSpinner(false);
      setIsSending(false);
    }
  };

  return (
    <>
      <div className="background-mask" onClick={() => onClose()}></div>
      <div className="filter-popup">
        <div className="close-button-container">
          <button className="close-button" onClick={() => onClose()}>
            <i className="bi bi-x-circle"></i>
          </button>
        </div>
        <div className="filter-header">
          <h2>
            Upload and Send {criminalOrder ? "Criminal" : "Reference"} Final
            Report
          </h2>
          <div>
            Please upload a pdf that will be made available to the client.
            Email(s) will be sent to client upon sending.
          </div>
          <br></br>
          {(criminalOrder?.finalReportUrl ||
            referenceOrder?.finalReportUrl) && (
            <div className="error">
              You will be replacing the existing final report. Save the initial
              download to the candidate’s details if you need to keep a record.
            </div>
          )}
        </div>
        <br></br>
        <div className="dialog-container">
          <div className="field-long" style={{ flex: "0 0 45%" }}>
            <label className="field-label">Client Name:</label>
            <div className="field-input">
              <input
                className="form-control"
                type="text"
                value={client.clientName}
                disabled
              />
            </div>
          </div>
          <div className="field-long" style={{ flex: "0 0 100%" }}>
            <label className="field-label">Send By Mail? :</label>
            <div>
              <input
                className="form-check-input"
                type="checkbox"
                checked={byMail}
                onChange={() => setByMail(!byMail)}
              />
            </div>
          </div>
          <div style={{ fontSize: "small" }}>
            Client prefers reports by{" "}
            <b> {DELIVERY_OPTION_MAP[client.deliveryOptionID ?? 3]} </b>
          </div>
          {byMail && (
            <div style={{ paddingTop: "10px" }}>
              <div style={{ fontSize: "small" }}>
                Admin is in charge of mailing report. 'Send Report' will
                complete the {criminalOrder ? "Criminal" : "Reference"} Order
                and add a Final Report Date. Final Report PDF will be downloaded
                to the browser.{" "}
                {criminalOrder ? "It will also now be invoiced." : ""}
              </div>
            </div>
          )}
          <div className="field-long" style={{ flex: "0 0 100%" }}>
            <button
              className="smbtn btn-grey"
              onClick={() => handleFormUpload()}
            >
              Upload Form
            </button>
            <div className="field-input">
              {pdfUrl && (
                <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
                  View Report{" "}
                  <i className="btn-icon bi bi-box-arrow-up-right"></i>
                </a>
              )}
            </div>
          </div>
          {errorMessage && <div className="error-message">{errorMessage}</div>}
        </div>
        <div className="filter-actions">
          <button
            className={`smbtn btn-green ${
              !pdfUrl || isSending || loadingSpinner ? "disabled" : ""
            }`}
            onClick={sendFinalReport}
            disabled={!pdfUrl || isSending || loadingSpinner}
          >
            Send Report
          </button>
        </div>
      </div>
      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
    </>
  );
};

export default UploadSendFinalReportPopup;
